import { Button } from "@freightsimple/ui-core/Controls/Button";
import { ControlPresentationProvider } from "@freightsimple/ui-core/Controls/ControlPresentationProvider";
import { FormItem } from "@freightsimple/ui-core/FormLayout/FormItem";
import { Heading } from "@freightsimple/ui-core/Typography/Heading";
import { Link } from "@freightsimple/ui-core/Typography/Link";
import { Logo } from "@freightsimple/ui-core/Display/Logo";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import { TextInput } from "@freightsimple/ui-core/Controls/TextInput";
import { useSelector } from "react-redux";
import { useState } from "react";
import type { RootState } from "@/store";
import Animation from "@/Shared/Animations/Animation";
import { useAuthenticationRequestResetPassword } from "../Hooks/useAuthenticationRequestResetPassword";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// A local, minimal check so this renovated screen doesn't import from the
// Contacts feature (fs/no-cross-feature-imports). We only need to catch an
// obviously-malformed address before hitting the API.
function emailErrorMessage(email: string): string | undefined {
  if (email === "") {
    return "Required";
  }
  if (!EMAIL_REGEX.test(email)) {
    return "Please enter a valid email address";
  }
  return undefined;
}

interface AuthenticationRequestResetProps {
  onBackToSignIn: () => void;
}

export function AuthenticationRequestReset(
  props: AuthenticationRequestResetProps,
) {
  const { onBackToSignIn } = props;
  const emailAddressForReset = useSelector(
    (state: RootState) => state.authentication.emailAddressForReset,
  );

  const [email, setEmail] = useState(emailAddressForReset);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);
  const [wasSuccess, setWasSuccess] = useState(false);
  const { requestReset } = useAuthenticationRequestResetPassword();

  async function onRequestResetPassword() {
    if (loading) {
      return;
    }
    const validationError = emailErrorMessage(email);
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }
    setErrorMessage(undefined);
    setLoading(true);
    const success = await requestReset(email);
    if (success) {
      setWasSuccess(true);
    } else {
      setErrorMessage("Something went wrong. Please try again.");
    }
    setLoading(false);
  }

  if (wasSuccess) {
    return (
      <Stack gap={6}>
        <Logo />
        <Stack gap={4}>
          <Heading level={1} size="4xl" weight="semibold">
            Reset Password Email Sent
          </Heading>
          <Stack gap={2}>
            <T size="sm" color="secondary">
              Please check your email to find the next steps for resetting your
              password
            </T>
            <T size="sm" color="muted">
              (and please check your spam if you can&apos;t find it at first)
            </T>
          </Stack>
        </Stack>
        <Stack align="center">
          <Animation
            pathToAnimation="/animations/email-sent.json"
            lottieFilesUrl="https://lottiefiles.com/6391-email-sent"
            loop
            speed={0.5}
            width={200}
          />
        </Stack>
        <Button
          primary
          fullWidth
          onClick={onBackToSignIn}
          data-testid="request-reset-back-button"
        >
          Back to sign in
        </Button>
      </Stack>
    );
  }

  return (
    <ControlPresentationProvider presentation="underlined">
      {/* Pressing Enter submits; preventDefault stops the browser's native GET
          navigation and drives the same path as the button. */}
      <form
        onSubmit={(event) => {
          event.preventDefault();
          onRequestResetPassword();
        }}
        noValidate
      >
        <Stack gap={6}>
          <Stack gap={10}>
            <Logo />
            <Stack gap={1}>
              <Heading level={1} size="4xl" weight="semibold">
                Request Password Reset
              </Heading>
              <T size="sm" color="secondary">
                Enter your email address below. If an account exists, you will
                receive an email containing instructions on how to reset your
                password.
              </T>
            </Stack>
          </Stack>

          <Stack gap={5}>
            <FormItem label="Email" fillWidth emphasizeLabel>
              <TextInput
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="username"
                name="forgot_password_email"
                width="full"
                placeholder="you@company.com"
                data-testid="request-reset-email"
              />
            </FormItem>
          </Stack>

          <Stack gap={4}>
            <Button
              primary
              fullWidth
              size="large"
              onClick={onRequestResetPassword}
              loading={loading}
              disabled={loading}
              disabledMessage="Sending reset instructions…"
              data-testid="request-reset-button"
            >
              Request Reset Password
            </Button>

            {errorMessage && (
              <T color="red" align="center">
                {errorMessage}
              </T>
            )}

            <Stack align="center">
              <T color="secondary">
                Remembered it?{" "}
                <Link onClick={onBackToSignIn}>Back to sign in</Link>
              </T>
            </Stack>
          </Stack>
        </Stack>
      </form>
    </ControlPresentationProvider>
  );
}
